import { ActionType } from "@sp-crm/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import {
    ActivityTypeEditor,
    activityTypesDescription,
} from "components/manage/agency-settings/activity-type-editor";
import { trpc } from "components/rpc/trpc";
import { invalidateTaskTypes } from "components/tasks/invalidate-task-types";
import { useOfficeTaskTypeState } from "components/tasks/use-office-task-type-state";
import { Checkbox } from "components/ui/checkbox";
import { ErrorMessage } from "components/ui/error-message";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { Spinner } from "components/ui/spinner";
import React, { ChangeEvent, useCallback } from "react";
import {
    useCurrentRegion,
    useIsAllowed,
    useIsAllowedInWholeTenant,
    useRegionId,
} from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { AgencySettingsLink } from "./agency-settings-link";

export const RegionSettingsActivityTypes: React.FC<unknown> = () => {
    const region = useCurrentRegion();
    const regionId = useRegionId();
    const canManageAgencySettings = useIsAllowedInWholeTenant(ActionType.UpdateTenant);
    const canModifyActivityTypes = useIsAllowed(ActionType.ModifyTaskType);
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();

    const stateQuery = useOfficeTaskTypeState(regionId);

    const onScopeChange = () => {
        void invalidateTaskTypes(queryClient);
    };

    const customize = useMutation({
        ...trpcClient.taskType.customizeOfficeTaskTypes.mutationOptions(),
        onSuccess: onScopeChange,
    });
    const revert = useMutation({
        ...trpcClient.taskType.revertOfficeTaskTypes.mutationOptions(),
        onSuccess: onScopeChange,
    });

    const overridden = stateQuery.data?.overridden ?? false;
    const customizable = stateQuery.data?.customizable ?? false;
    const sharedPool = stateQuery.data?.sharedPool ?? false;
    const pending = customize.isPending || revert.isPending;
    const mutationFailed = customize.isError || revert.isError;

    const handleOverrideChange = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            if (!regionId) {
                return;
            }
            if (e.target.checked) {
                handleEvent("activity-type-office-customize");
                customize.mutate({ regionId });
            } else {
                const confirmed = await fancyConfirm(
                    "Use agency settings?",
                    `This will hide the activity types specific to ${region?.name} and use the agency activity types instead. Activities already logged keep their activity types, and this office's names, record types, archived types and its own activity types come back if you switch back later.`,
                    "Yes, use agency settings",
                    "No, cancel",
                    { confirmStyle: "question" },
                );
                if (confirmed) {
                    handleEvent("activity-type-office-revert");
                    revert.mutate({ regionId });
                }
            }
        },
        [customize, revert, regionId, region],
    );

    if (!regionId) {
        return null;
    }
    if (stateQuery.isPending) {
        return <Spinner />;
    }
    if (stateQuery.isError || !stateQuery.data) {
        return (
            <ErrorMessage component="RegionSettingsActivityTypes">
                <pre>{JSON.stringify(stateQuery.error, null, 2)}</pre>
            </ErrorMessage>
        );
    }

    const canEditOffice = overridden && customizable && canModifyActivityTypes;

    return (
        <ContentFull>
            <ContentHeader>{`Activity types - ${region?.name}`}</ContentHeader>
            <ContentSubheader>{activityTypesDescription}</ContentSubheader>
            <div className="space-y-4">
                {customizable || overridden ? (
                    <Checkbox
                        checked={overridden}
                        disabled={pending || !canModifyActivityTypes}
                        onChange={handleOverrideChange}
                        label={
                            <p>
                                Use specific activity types for{" "}
                                <strong>{region?.name}</strong>
                            </p>
                        }
                    />
                ) : null}
                {mutationFailed ? (
                    <InlineBanner type="error">
                        The change could not be saved. Please try again.
                    </InlineBanner>
                ) : null}
                {!overridden ? (
                    <InlineBanner type="info">
                        {sharedPool && !customizable
                            ? `${region?.name} shares referral sources or communities with other offices, so the activity types below apply to the whole agency and are managed in the `
                            : "The activity types below apply to the whole agency and are managed in the "}
                        <AgencySettingsLink
                            canManageAgencySettings={canManageAgencySettings}
                            to="/settings/agency/tasks"
                        />
                        {customizable && canModifyActivityTypes
                            ? ". Check the box above to customize them for this office."
                            : "."}
                    </InlineBanner>
                ) : null}
                {overridden && !customizable ? (
                    <InlineBanner type="info">
                        {`${region?.name} now shares referral sources or communities with other offices, so its activity types can no longer be edited here. The office activity types stay in effect until you switch back to the agency settings.`}
                    </InlineBanner>
                ) : null}
                <ActivityTypeEditor
                    scope={
                        overridden ? { kind: "office", regionId } : { kind: "agencyWide" }
                    }
                    disabled={!canEditOffice}
                />
            </div>
        </ContentFull>
    );
};
